import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@insolution/auth-data-access';
import { GetBackendSignalGQL, OnBackendSignalChangedGQL } from '@insolution/graphql-data-access';
import { LocalStorageService } from '@insolution/store-data-access';
import { APP_VERSION } from '@insolution/version';
import { firstValueFrom, Subscription } from 'rxjs';
import { IOutdatedAppVersion } from '../interfaces/outdated-app-version.interface';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly latestAppVersionSignalKey = 'latest_app_version';
  private readonly authService = inject(AuthService);
  private readonly getBackendSignalGQL = inject(GetBackendSignalGQL);
  private readonly onBackendSignalChangedGQL = inject(OnBackendSignalChangedGQL);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  private readonly maxReloadAttempts = 2;
  private readonly outdatedAppVersionKey = 'outdatedAppVersion';
  private subscription: Subscription | null = null;

  public isCurrentVersionOutdated(): boolean {
    return !!this.getOutdatedVersion();
  }

  public getOutdatedVersion(): IOutdatedAppVersion | null {
    return this.localStorageService.getItem<IOutdatedAppVersion>(this.outdatedAppVersionKey);
  }

  public async startVersionCheck(): Promise<void> {
    await this.authService.waitForLoginStatus();
    try {
      await this.checkLatestAppVersion();
    } catch (error) {
      console.error('Error checking latest app version:', error);
    }
    this.subscribeToLatestAppVersion();
  }

  private async checkLatestAppVersion(): Promise<void> {
    const result = await firstValueFrom(
      this.getBackendSignalGQL.fetch({ key: this.latestAppVersionSignalKey }, { fetchPolicy: 'network-only' })
    );
    const latestVersion = result.data.getBackendSignal?.value;

    if (latestVersion) {
      this.reloadIfNewer(latestVersion);
    }
  }

  private subscribeToLatestAppVersion(): void {
    if (this.subscription) {
      return;
    }

    this.subscription = this.onBackendSignalChangedGQL.subscribe({ key: this.latestAppVersionSignalKey }).subscribe({
      next: (result) => {
        const latestVersion = result.data?.onBackendSignalChanged?.value;

        if (latestVersion) {
          this.reloadIfNewer(latestVersion);
        }
      },
      error: (error) => console.error('Error watching latest app version:', error),
    });
  }

  private reloadIfNewer(latestVersion: string): void {
    if (!this.isNewerVersion(latestVersion, APP_VERSION)) {
      this.localStorageService.removeItem(this.outdatedAppVersionKey);
      this.localStorageService.removeItem(this.reloadAttemptsKey(latestVersion));
      return;
    }

    const reloadAttemptsKey = this.reloadAttemptsKey(latestVersion);
    const reloadAttempts = this.localStorageService.getItem<number>(reloadAttemptsKey) ?? 0;
    if (reloadAttempts >= this.maxReloadAttempts) {
      this.storeOutdatedVersion(latestVersion);
      this.router.navigate(['/availability']);
      return;
    }

    this.localStorageService.storeItem(reloadAttemptsKey, reloadAttempts + 1);
    this.document.defaultView?.location.reload();
  }

  private isNewerVersion(latestVersion: string, currentVersion: string): boolean {
    if (latestVersion === currentVersion) {
      return false;
    }

    const latest = this.parseVersion(latestVersion);
    const current = this.parseVersion(currentVersion);
    if (!latest || !current) {
      return latestVersion !== currentVersion;
    }

    for (let index = 0; index < latest.length; index += 1) {
      if (latest[index] > current[index]) {
        return true;
      }

      if (latest[index] < current[index]) {
        return false;
      }
    }

    return false;
  }

  private parseVersion(version: string): [number, number, number, number] | null {
    const match = /^v?(\d+)\.(\d+)\.(\d+)(?:-dev\.(\d+))?/.exec(version);
    if (!match) {
      return null;
    }

    // A release (no -dev suffix) outranks any -dev pre-release of the same x.y.z, hence Infinity.
    const devBuild = match[4] ? Number(match[4]) : Number.POSITIVE_INFINITY;
    return [Number(match[1]), Number(match[2]), Number(match[3]), devBuild];
  }

  private reloadAttemptsKey(latestVersion: string): string {
    return `appVersionReloadAttempts:${latestVersion}`;
  }

  private storeOutdatedVersion(latestVersion: string): void {
    this.localStorageService.storeItem<IOutdatedAppVersion>(this.outdatedAppVersionKey, {
      currentVersion: APP_VERSION,
      latestVersion,
    });
  }
}
